import { controlCharsObject } from "@quickpoint.me/shared";
import {
	type ReactElement,
	type RefObject,
	memo,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import styled, { ThemeProvider } from "styled-components";
import { FilterFonts, FilterName, FilterSelector } from "../../state/filter";
import {
	AuthorSelectedElementAtom,
	IsInPlayWrightTest,
	PageElementsFamily,
	PageSizeWithPaddingSelector,
	StickyHeaderHeightAtom,
	TapIdContainers,
	createLocation,
	pageHasWidescreenState,
} from "../../state/page";
import { AllLoaded, NumberOfPages } from "../../state/selectors";
import {
	ConstrainDimensionsSelector,
	DebugMode,
	IsInAuthor,
	IsPreviewGenerator,
	TapDirection,
} from "../../state/ui";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRecoilCallback, useRecoilValue } from "recoil";
// @ts-expect-error: No types for this package
import smartquotes from "smartquotes";
import type { ElementsProps } from "../../@types/viewerati";
import { ElementsTimeline } from "../../Display";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import useAnimation from "../../lib/animations/animationsHook";
import { startingPoint } from "../../lib/cssHelpers";
import { displayElementsCssRules } from "../../lib/cssTemplates";
import findSplits from "../../lib/findPageSplits";
import { NavigationType } from "../../state/navigation";
import { PageTimelineContext, PositionContext } from "./DisplayElements";
import DisplayGrid from "./DisplayGrid";
import DisplaySplitPage from "./DisplaySplitPage";

import { TUFTE_VERTICAL_PAD_FRACTION, computeMediaWeight, countWords, isMediaType, isTextType } from "../../lib/gridTypes";
import { groupListRuns } from "../../lib/listRuns";
import { sendElementClickToAuthor } from "../../functionalComponents/MessageHandler";
import SafariWrapper from "./SafariWrapper";

/**
 * Gets the state holding the elements that are to be displayed in the current page
 * and returns a JSX element containing them with CSS and theming applied.
 * @returns JSX
 */

export const StyledDisplayElements = styled.div<
	ElementsProps & { $hasWidescreen?: boolean; $baseFontSizeRaw: number; $forceSingleColumn: boolean; $authorSelectedAtomId?: number | null }
>`
  display: grid;
  position: ${(props) => (props.$navigationType === "scroll" ? "relative" : "absolute")};
	height: ${(props) =>
		props.$navigationType === "scroll" && !props.$isInLandscape
			? "100%"
			: `${props.$height}px`};
  width:100%;
  grid-auto-columns: 1fr;
  ${(props) =>
		`${displayElementsCssRules({
			baseFontSize: props.$baseFontSize,
			baseFontSizeRaw: props.$baseFontSizeRaw,
			fonts: props.$fonts,
			theme: props.theme,
			isOverflowing: props.$isOverflowing ? props.$isOverflowing : false,
			isInLandscape: props.$isInLandscape ? props.$isInLandscape : false,
			stickyHeaderHeight: props.$stickyHeaderHeight,
		})}`};
  /* Frame padding is handled by PageSizeWithPaddingSelector — zero out CSS padding to avoid double-application */
  padding-inline: 0;
  padding-block: 0;
  align-content: ${(props) => startingPoint(props.theme.startingPoint, props.$isOverflowing, props.$isInLandscape, props.$forceSingleColumn)};
  padding-top: ${(props) => props.$stickyHeaderHeight}px;
  ${(props) => props.$hasWidescreen && "padding: 0;"}
  /* Media is coupled to the text measure: never narrower than the text
     column, wider (up to --media-max-width) when text is sparse.
     Each declaration reads a --qp-el-* variable first so explicit user
     modifiers (align:, width:) can override these defaults from the
     element level despite this rule's higher specificity */
  & > .image,
  & > .video,
  & > .chart {
    max-width: var(--qp-el-max-width, max(min(65%, 72ch), var(--media-max-width, 100%)));
    justify-self: var(--qp-el-justify, center);
  }
  & > .paragraph,
  & > .title,
  & > .subtitle,
  & > .supertitle {
    max-width: var(--qp-el-max-width, min(65%, 72ch));
    justify-self: var(--qp-el-justify, center);
    text-align: var(--qp-el-align, left);
  }
  /* maps read like figures: keep them on the text measure, centered */
  & > .gMap {
    max-width: var(--qp-el-max-width, min(65%, 72ch));
    justify-self: var(--qp-el-justify, center);
  }
  /* boxes share the measure; buttons centre at their intrinsic width */
  & > .box {
    max-width: var(--qp-el-max-width, min(65%, 72ch));
    justify-self: var(--qp-el-justify, center);
  }
  & > .audio {
    max-width: var(--qp-el-max-width, min(65%, 46ch));
    justify-self: var(--qp-el-justify, center);
  }
  & > .Button {
    justify-self: var(--qp-el-justify, center);
  }
  /* a video sharing the slide with a map would otherwise take the full
     measure at 16:9 and squeeze the map to a sliver — give it a height
     budget instead and let the aspect ratio derive the width */
  &:has(> .gMap) > .video {
    max-width: min(45%, 50ch);
  }
  /* bullet runs share the text measure so list text aligns with body text;
     the markers hang outside the box, into the margin */
  & > ul.qp-list-run,
  & > ol.qp-list-run {
    width: var(--qp-el-max-width, min(65%, 72ch));
    justify-self: var(--qp-el-justify, center);
    margin: 0;
    padding: 0;
  }
  /* click-to-edit selection cue: white halo keeps the ring visible on any theme */
  ${(props) =>
		props.$authorSelectedAtomId != null &&
		`[data-atom-id="${props.$authorSelectedAtomId}"] {
    outline: 3px solid #2563eb;
    outline-offset: 3px;
    box-shadow: 0 0 0 7px rgba(255, 255, 255, 0.55);
  }`}
`;

const DisplayElementsChildren = memo(
	({
		pageElementsToDisplay,
		pageIndex,
		"aria-hidden": ariaHidden,
		scrollerRef,
	}: {
		pageElementsToDisplay: ReactElement<ElementProps>[];
		pageIndex: number;
		"aria-hidden"?: boolean;
		scrollerRef?: RefObject<HTMLDivElement>;
	}) => {
		const pageSize = useRecoilValue(PageSizeWithPaddingSelector);
		const baseFontSize = pageSize.baseFontSize;
		const { contextSafe } = useGSAP();
		const direction = useRecoilValue(TapDirection);
		const elementsTimeline = contextSafe(gsap.timeline);
		const filterName = useRecoilValue(FilterName);
		const filter = useRecoilValue(FilterSelector(filterName));
		const fonts = useRecoilValue(FilterFonts);
		const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
		const isPreviewer = useRecoilValue(IsPreviewGenerator);
		const isInPlayWrightTest = useRecoilValue(IsInPlayWrightTest);
		const isInAuthor = useRecoilValue(IsInAuthor);
		const authorSelected = useRecoilValue(AuthorSelectedElementAtom);
		const authorSelectedAtomId =
			isInAuthor && authorSelected?.pageIndex === pageIndex
				? authorSelected.atomId
				: null;
		const navigationType = useRecoilValue(NavigationType);
		const numberOfPages = useRecoilValue(NumberOfPages);
		const overflowRef = useRef<HTMLDivElement>(null);
		const position = useContext(PositionContext);
		const rows = pageElementsToDisplay.filter(
			(el) => el.props.type === controlCharsObject["---"].controlChar,
		);
		const [isOverflowing, setIsOverflowing] = useState(false);
		const allLoaded = useRecoilValue(AllLoaded);
		const tapIdContainers = useRecoilValue(TapIdContainers(pageIndex));
		const debugMode = useRecoilValue(DebugMode);
		const stickyHeaderHeight = useRecoilValue(StickyHeaderHeightAtom);

		const pageHasWidescreen = useRecoilValue(pageHasWidescreenState(pageIndex));

		// Tufte media sizing: constrain media width on non-grid pages with mixed text+media
		const [mediaMaxWidth, setMediaMaxWidth] = useState(100);
		const computeMediaWidth = useRecoilCallback(
			({ snapshot }) =>
				() => {
					if (rows.length > 0 || !pageSize.landscape) return 100;
					const hasMedia = pageElementsToDisplay.some(el => isMediaType(el.props.type));
					if (!hasMedia) return 100;
					const hasText = pageElementsToDisplay.some(el => isTextType(el.props.type));
					if (!hasText) return 100;
					let totalWords = 0;
					for (const el of pageElementsToDisplay) {
						if (isTextType(el.props.type)) {
							const key = createLocation(pageIndex, el.props.atomId);
							const loadable = snapshot.getLoadable(PageElementsFamily(key));
							if (loadable.state === "hasValue") {
								totalWords += countWords(loadable.getValue().data.content);
							}
						}
					}
					return computeMediaWeight(totalWords, baseFontSize);
				},
			[pageElementsToDisplay, pageIndex, pageSize.landscape, baseFontSize, rows.length],
		);

		useLayoutEffect(() => {
			setMediaMaxWidth(computeMediaWidth());
		}, [computeMediaWidth]);

		// Click-to-edit: when in author mode, clicking an element sends its lineNumber to author
		const handleElementClick = useRecoilCallback(
			({ snapshot, set }) =>
				async (e: React.MouseEvent) => {
					if (!isInAuthor) return;
					const target = (e.target as HTMLElement).closest("[data-atom-id]");
					if (!target) return;
					const atomId = Number(target.getAttribute("data-atom-id"));
					if (Number.isNaN(atomId)) return;
					const key = createLocation(pageIndex, atomId);
					const elementState = await snapshot.getPromise(PageElementsFamily(key));
					const data = elementState.data;
					if (data.lineNumber == null) return;
					set(AuthorSelectedElementAtom, { pageIndex, atomId });
					sendElementClickToAuthor("QP", {
						lineNumber: data.lineNumber,
						pageIndex,
						elementType: data.type,
						content: typeof data.content === "string" ? data.content : "",
					});
				},
			[isInAuthor, pageIndex],
		);

		const pageTimeline = useAnimation({
			direction,
			elRef: overflowRef,
			filter,
			isInAuthor,
			isInLandscape: pageSize.landscape,
			isInPlayWrightTest,
			isOverflowing,
			isPreviewer,
			navigationType,
			numberOfPages,
			onePercent: {
				width: pageSize.onePercentWidth,
				height: pageSize.onePercentHeight,
			},
			pageIndex,
			position,
			tapIdContainers,
			scrollerRef,
		});

		// add smartquotes to each element when the displayed content changes
		useEffect(() => {
			const styledDisplayElements = document.getElementsByClassName(
				StyledDisplayElements.styledComponentId,
			);
			for (const el of Array.from(styledDisplayElements)) {
				smartquotes(el);
			}
		}, [pageElementsToDisplay, pageIndex, allLoaded]);

		// check if content is overflowing on mobile when using middleOut layout
		useLayoutEffect(() => {
			allLoaded;
			pageIndex;
			pageSize;
			pageElementsToDisplay;
			if (overflowRef.current) {
				const displayChildrenScrollHeight = overflowRef.current.scrollHeight;
				const displayChildrenHeight = overflowRef.current.clientHeight;
				const overflowing = displayChildrenScrollHeight > displayChildrenHeight;
				if (!pageSize.landscape) setIsOverflowing(overflowing);

				// //console.log("displayChildrenScrollHeight: ", displayChildrenScrollHeight);
				// //console.log("displayChildrenHeight:", displayChildrenHeight);
				// //console.log(overflowing);
				// //console.log("--------------");
			}
		}, [pageSize, pageElementsToDisplay, pageIndex, allLoaded]);

		const splitIndices: number[] = [];
		pageElementsToDisplay.forEach((el, idx) => {
			if (el.props.type === controlCharsObject["|"].controlChar) {
				splitIndices.push(idx);
			}
		});

		const rowsIndices: number[] = [];
		pageElementsToDisplay.forEach((el, idx) => {
			if (el.props.type === controlCharsObject["---"].controlChar) {
				rowsIndices.push(idx);
			}
		});

		const endOfGridIndices: number[] = [];
		pageElementsToDisplay.forEach((el, idx) => {
			if (el.props.type === controlCharsObject["-->"].controlChar) {
				endOfGridIndices.push(idx);
			}
		});

		const pageSplits = findSplits({
			rowsIndices,
			splitIndices,
			endOfGridIndices,
			numberOfLines: pageElementsToDisplay.length,
			getPageSplits: true,
		});

		// if (position === "left") {
		//   //console.log("Splits: ", splitIndices);
		//   //console.log("Rows: ", rowsIndices);
		//   //console.log("End: ", endOfGridIndices);
		//   //console.log("PageSplits: ", pageSplits);
		//   //console.log("---------------------");
		// }

		if (pageSplits.length > 0) {
			return (
				<DisplaySplitPage
					pageElementsToDisplay={pageElementsToDisplay}
					pageIndex={pageIndex}
					pageSplits={pageSplits}
					scrollerRef={scrollerRef}
					aria-hidden={ariaHidden}
				/>
			);
		}
		return (
			<SafariWrapper
				variant="elements"
				aria-hidden={ariaHidden}
				$hasWidescreen={pageHasWidescreen}
			>
				<PageTimelineContext.Provider value={pageTimeline}>
					<ThemeProvider theme={filter}>
						<ElementsTimeline.Provider value={elementsTimeline()}>
							{debugMode && (
								<div
									style={{ background: "white", color: "black", zIndex: 1000 }}
								>
									{position}
									<br />
									{direction}
								</div>
							)}
							{/* biome-ignore lint/a11y/useValidAriaRole: Hide from screen readers*/}
							<StyledDisplayElements
								role="none"
								tabIndex={-1}
								ref={overflowRef}
								onClick={isInAuthor ? handleElementClick : undefined}
								$authorSelectedAtomId={authorSelectedAtomId}
								$baseFontSize={baseFontSize}
								$baseFontSizeRaw={pageSize.baseFontSizeRaw}
								$fonts={fonts}
								$height={pageSize.height}
								$constrainDimensions={constrainDimensions}
								$isInLandscape={pageSize.landscape}
								$isOverflowing={isOverflowing}
								$isPreviewer={isPreviewer}
								$stickyHeaderHeight={stickyHeaderHeight}
								$width={pageSize.width}
								$navigationType={navigationType}
								$forceSingleColumn={pageSize.forceSingleColumn}
								className={`display-elements-children ${position} ${isPreviewer ? "previewer" : ""}`}
								id={`display-elements-children-${pageIndex}`}
								data-testid="DisplayElementsChildren"
								key={`DisplayElements-${pageIndex}-${position}`}
								$hasWidescreen={pageHasWidescreen}
							style={
								mediaMaxWidth < 100
									? ({
										'--media-max-width': `${mediaMaxWidth}%`,
										// vertical breathing room, same rhythm as weighted grids
										paddingTop: `${stickyHeaderHeight + Math.round(pageSize.height * TUFTE_VERTICAL_PAD_FRACTION)}px`,
										paddingBottom: `${Math.round(pageSize.height * TUFTE_VERTICAL_PAD_FRACTION)}px`,
									} as React.CSSProperties & Record<string, string>)
									: undefined
							}
							>
								{rows.length > 0 ? (
									<DisplayGrid pageElementsToDisplay={pageElementsToDisplay} />
								) : (
									groupListRuns(pageElementsToDisplay)
								)}
							</StyledDisplayElements>
						</ElementsTimeline.Provider>
					</ThemeProvider>
				</PageTimelineContext.Provider>
			</SafariWrapper>
		);
	},
);

export default DisplayElementsChildren;
